/**
 * Layout da cidade — fonte única de verdade (modelo de QUADRA).
 *
 * Cada quadra é o terreno entre as vias. Dentro dela, de fora para dentro:
 *   1. CALÇADA (anel pavimentado junto às ruas, onde pedestres andam)
 *   2. PARQUE  (anel verde com árvores ao redor do prédio)
 *   3. PRÉDIO  (centrado na quadra)
 *
 * Esse modelo garante que os prédios fiquem dentro das quadras com calçada,
 * e dá os anéis certos para distribuir as árvores (na calçada e no parque),
 * em vez de espalhá-las aleatoriamente (inclusive sobre as ruas).
 */

import { COLORS, ROADS, WORLD, isGridLine, mixHex, roadsFor } from '../config.js';
import { ACTIVE_CITY } from './cities.js';

// LINHAS DA GRADE (sem os tocos de acesso — ver config.isGridLine): é daqui que saem
// as colunas/linhas dos lotes, então a lista tem de ser exatamente a da grade.
const V = ROADS.vertical.filter(isGridLine).map((r) => r.x); // Mexican 6×4: [-106,-69,-37,0,37,69,106]
const H = ROADS.horizontal.filter(isGridLine).map((r) => r.z);
const HALF = ROADS.width / 2; // meia-largura da via

/** Nome/id da cidade ativa (uma das 4 — ver cities.js). */
export const CITY_NAME = ACTIVE_CITY.name;
export const CITY_ID = ACTIVE_CITY.id;
/** Quadra da rotatória/praça (coluna,linha) desta cidade — `null` quando a cidade
 *  não tem praça (Mexican Town). Tudo que depende da praça fica desligado. */
const RB = ACTIVE_CITY.roundabout || null;

/** Largura da calçada e do anel de parque (em unidades). */
export const SIDEWALK = 5;
export const PARK = 4;

/** Terreno (land) de uma quadra que vai das colunas c0..c1 e linhas r0..r1, numa
 *  malha viária qualquer (a da cidade ativa por padrão — ver landRect). */
function landRectIn(roads, c0, c1, r0, r1) {
  const vs = roads.vertical.filter(isGridLine).map((r) => r.x);
  const hs = roads.horizontal.filter(isGridLine).map((r) => r.z);
  const half = roads.width / 2;
  const left = vs[c0] + half;
  const right = vs[c1 + 1] - half;
  const top = hs[r0] + half;
  const bottom = hs[r1 + 1] - half;
  return {
    x: (left + right) / 2,
    z: (top + bottom) / 2,
    w: right - left,
    d: bottom - top,
    left,
    right,
    top,
    bottom
  };
}

/** Terreno (land) de uma quadra que vai das colunas c0..c1 e linhas r0..r1. */
function landRect(c0, c1, r0, r1) {
  const left = V[c0] + HALF;
  const right = V[c1 + 1] - HALF;
  const top = H[r0] + HALF;
  const bottom = H[r1 + 1] - HALF;
  return {
    x: (left + right) / 2,
    z: (top + bottom) / 2,
    w: right - left,
    d: bottom - top,
    left,
    right,
    top,
    bottom
  };
}

/**
 * Dimensões 3D do PRÉDIO de um lote — MESMA conta usada para montar BUILDINGS:
 * largura/profundidade = terreno da quadra menos calçada+parque; altura = lot.h.
 * Single-cell → 18×18 de base; lote mesclado (2 linhas, p.ex. o prédio alto da
 * última coluna) → fundo bem maior. Exportada p/ a prévia admin (models.html)
 * renderizar cada prédio EXATAMENTE na proporção do jogo.
 *
 * `city` = a cidade DONA do lote (padrão: a ativa). A prévia percorre os lotes de
 * TODAS as cidades, e medir um lote de 5 colunas na grade de 4 (ou vice-versa)
 * daria NaN — por isso a malha vem da cidade do lote, não da ativa. */
export function buildingDims(lot, city = ACTIVE_CITY) {
  const land = landRectIn(roadsFor(city), lot.c0, lot.c1, lot.r0, lot.r1);
  let w = land.w - 2 * SIDEWALK - 2 * PARK;
  let d = land.d - 2 * SIDEWALK - 2 * PARK;
  // mesma limitação ao canteiro do jogo (lote mesclado não passa dos postes) — só p/ a cidade
  // ATIVA, onde districtBed/DISTRICTS batem com o lote
  const bed = city === ACTIVE_CITY ? districtBed(lot) : null;
  if (bed) { w = bed.w - 14; d = bed.d - 14; } // canteiro empacotado dirige o tamanho (espelha BUILDINGS)
  return { w, d, h: lot.h };
}

/** Quadras com prédio (índices coluna/linha + tipo + rótulo + altura) — da cidade
 *  ativa. Cada cidade tem seus próprios lots (referências separadas — cities.js). */
const LOTS = ACTIVE_CITY.lots;

/** Quadras-parque (sem prédio, preenchidas de árvores) — da cidade ativa. */
const PARK_CELLS = ACTIVE_CITY.parkCells;

/* ------------------------------------------------------------------ */
/*  Distritos (superquadras)                                           */
/* ------------------------------------------------------------------ */

/**
 * DISTRITOS (cities.js → `districts`) — grupos 2×2 de quadras que viram UMA
 * superquadra: as ruas internas somem e o miolo vira parque de pedestre.
 *
 * `land` cobre o retângulo INTEIRO do grupo (82×82 no 4×4), portanto INCLUI as vias
 * internas — é essa a diferença para o `landRect` de uma quadra normal, e é o que
 * permite perguntar "este ponto/rua está dentro de um distrito?".
 *
 * `axisX`/`axisZ` = os eixos das ruas que somem. ATENÇÃO: eles NÃO são as passarelas do
 * parque. Isso valia enquanto os canteiros seguiam a grade; depois que passaram a ser
 * EMPACOTADOS (districtBed) o vão entre canteiros deixou de cair no eixo da rua antiga.
 * Quem quiser a planta de quem anda a pé deve usar DISTRICT_WALK.
 */
// largura da passarela = o vão entre as gramas de dois canteiros vizinhos. Sai da
// própria conta do districtBed: grama = canteiro − 6 ⇒ sobram 3u de cada lado.
export const DISTRICT_PATH = 6;

export const DISTRICTS = (ACTIVE_CITY.districts || []).map((d) => {
  const land = landRect(d.c0, d.c1, d.r0, d.r1);
  return {
    ...d,
    land,
    // LUZ do poste: a cor do distrito CLAREADA. A cor pura não serve — o globo é
    // emissivo e precisa estourar no bloom (limiar 0.86) p/ ler como luz; um azul ou
    // roxo cheio vira uma bola escura. Clarear preserva o matiz e devolve o brilho.
    lamp: d.color ? mixHex(d.color, '#ffffff', 0.5) : null,
    axisX: V[d.c0 + 1],
    axisZ: H[d.r0 + 1],
    // miolo do parque: a superquadra menos o anel de calçada do perímetro
    inner: {
      left: land.left + SIDEWALK, right: land.right - SIDEWALK,
      top: land.top + SIDEWALK, bottom: land.bottom - SIDEWALK
    }
  };
});

/** Distrito que contém a célula (coluna,linha) — `null` em cidade sem distritos. */
export function districtAt(col, row) {
  return (
    DISTRICTS.find((d) => col >= d.c0 && col <= d.c1 && row >= d.r0 && row <= d.r1) || null
  );
}

/** Distrito de uma QUADRA (pelo CANTEIRO que ela carrega; `null` fora de superquadra). */
export function districtOfBlock(block) {
  return (block && block.bed && block.bed.district) || null;
}

/** (x,z) cai DENTRO de uma superquadra? (inclui as vias internas que viram calçada.) */
export function insideDistrict(x, z) {
  return DISTRICTS.some(
    (d) => x > d.land.left && x < d.land.right && z > d.land.top && z < d.land.bottom
  );
}

/**
 * CANTEIRO de um lote dentro de uma superquadra: o pedaço do parque que cabe a ele,
 * do anel do perímetro até a beira da passarela. O prédio é CENTRADO NELE — e não na
 * célula da grade — porque as ruas que sumiram deixaram TODO o espaço extra do lado
 * de dentro: mantendo a posição antiga o prédio fica empurrado contra a borda da
 * quadra e a entrada perde o recuo (visível principalmente na fileira de baixo, cuja
 * porta dá para a rua). Um lote MESCLADO (o shopping, 2 linhas) atravessa o eixo, e
 * aí o canteiro dele ocupa o miolo inteiro naquele sentido.
 */
export function districtBed(lot) {
  const d = DISTRICTS.find(
    (x) => lot.c0 >= x.c0 && lot.c1 <= x.c1 && lot.r0 >= x.r0 && lot.r1 <= x.r1
  );
  if (!d) return null;
  // Canteiros EMPACOTADOS: dividem o d.land INTEIRO em nCols×nRows células iguais de 32×32, SEM
  // passarela (WALK=0) e sem recuo de perímetro. Assim a faixa de calçada de um canteiro ENCOSTA na
  // do vizinho (bandas se conectam) e a superquadra vira um bloco cinza-claro contínuo com quadrados
  // de grama de 32 em 32 — não sobra chão escuro. (d.inner deixa de ser usado aqui.)
  // MARGIN = recuo de perímetro = MEIA passarela interna: a calçada EXTERNA da superquadra
  // (MARGIN + faixa do canteiro) iguala a INTERNA (faixa + faixa = 6). d.land já cresceu 2*MARGIN
  // na grade (102×70), então as células continuam 32×32 e os prédios 18×18.
  const MARGIN = 3;
  const nCols = d.c1 - d.c0 + 1, nRows = d.r1 - d.r0 + 1; // 3 × 2
  const bedW = (d.land.w - 2 * MARGIN) / nCols; // (102-6)/3 = 32
  const bedD = (d.land.d - 2 * MARGIN) / nRows; // (70-6)/2 = 32
  const left = d.land.left + MARGIN + (lot.c0 - d.c0) * bedW;
  const right = left + (lot.c1 - lot.c0 + 1) * bedW;
  const top = d.land.top + MARGIN + (lot.r0 - d.r0) * bedD;
  const bottom = top + (lot.r1 - lot.r0 + 1) * bedD;
  return {
    left, right, top, bottom,
    x: (left + right) / 2, z: (top + bottom) / 2,
    w: right - left, d: bottom - top,
    district: d
  };
}

export const BUILDINGS = [];
export const BLOCKS = []; // toda quadra "viva" (com prédio ou parque) — p/ calçadas
export const DISTRICT_BEDS = []; // canteiros de grama das superquadras (1 por lote)

for (const lot of LOTS) {
  const land = landRect(lot.c0, lot.c1, lot.r0, lot.r1);
  const bed = districtBed(lot); // null fora de superquadra → prédio no centro da quadra
  let bw = land.w - 2 * SIDEWALK - 2 * PARK;
  let bd = land.d - 2 * SIDEWALK - 2 * PARK;
  // Na superquadra o prédio é CENTRADO no canteiro (bed) e postes/árvores seguem o bed (recuados
  // ~5.4 dos cantos). Um lote MESCLADO (shopping) tem land bem maior que o bed (inclui a via
  // interna coberta), então dimensionar pelo land faz o prédio passar dos postes. Limita ao bed
  // com a MESMA folga dos prédios de 1 célula (bed − 14.4 ⇒ single 32.5→18.1: `min` mantém 18).
  if (bed) {
    // Tamanho do prédio agora vem do CANTEIRO (bed − 14), não do passo da grade: com os canteiros
    // EMPACOTADOS (32×32) o land por célula ficou pequeno, então derivar de land encolheria o prédio.
    // bed 32 → 18×18 (grama 26, faixa 3 de cada lado, encostando na vizinha); shopping 64 → 50×18.
    bw = bed.w - 14;
    bd = bed.d - 14;
    // Grama JUSTA em volta do prédio (proporção da Acquisition): quadrado = footprint + 2*PARK,
    // CENTRADO no lote. Fonte ÚNICA lida pelo desenho (CityTexture) E pela navegação (inBed). Vale
    // também p/ o PARQUE (drawPlazaGround usa bed.grass p/ a cruz e buildPark p/ a vegetação).
    const gw = bw + 2 * PARK, gd = bd + 2 * PARK; // 18+8 = 26 numa célula normal
    bed.grass = {
      left: bed.x - gw / 2, right: bed.x + gw / 2,
      top: bed.z - gd / 2, bottom: bed.z + gd / 2,
      w: gw, d: gd
    };
  }
  BUILDINGS.push({
    x: bed ? bed.x : land.x,
    z: bed ? bed.z : land.z,
    w: bw,
    d: bd,
    h: lot.h,
    label: lot.label,
    type: lot.type || 'office',
    c0: lot.c0, c1: lot.c1, r0: lot.r0, r1: lot.r1, // quadra (coluna/linha)
    rot90: lot.rot90 || false, // gira o modelo 90° (ex.: shopping mesclado na horizontal)
    bed,
    land
  });
  // o CANTEIRO vai junto no BLOCK: quem planta (generateTrees) e quem ilumina
  // (LightPosts) precisa dele para acompanhar o prédio, e não a célula da grade
  BLOCKS.push({ land, kind: 'building', bed });
  if (bed) DISTRICT_BEDS.push(bed);
}

/* ------------------------------------------------------------------ */
/*  Superquadra: piso e malha de caminhada                             */
/* ------------------------------------------------------------------ */

/**
 * Medidas do PISO da superquadra. Moram AQUI (e não no CityTexture, que as desenha)
 * porque duas camadas dependem delas: o desenho do chão e a navegação a pé. Enquanto
 * o piso era só desenho dava para viver com dois conjuntos de números; agora que o
 * pedestre anda pela superquadra INTEIRA, divergir os dois é o bug clássico — gente
 * caminhando sobre o canteiro, ou DENTRO DE UM PRÉDIO.
 *
 * Foi exatamente o que aconteceu: o piso tinha um LARGO circular (raio 11) com um
 * JARDIM (raio 7) no encontro das passarelas, herdados de quando o eixo do distrito
 * era uma praça com fonte. A fonte saiu (ver Decor.createDecor) e o CityTexture parou
 * de desenhar o largo — mas a máscara continuou devolvendo "calçada" ali, POR CIMA do
 * canto dos canteiros. Só que os canteiros foram EMPACOTADOS (districtBed) e o eixo
 * deixou de cair no vão entre eles: o círculo passou a morder a pegada dos prédios.
 * MEDIDO na Mexican: 204 u² de "calçada" dentro de 13 edifícios (82 no shopping),
 * 5 nós e 8 arestas do SidewalkGraph dentro/através de prédios. Largo e jardim foram
 * REMOVIDOS do modelo — o piso da superquadra é o que o desenho mostra: tudo menos
 * os canteiros.
 */
export const DISTRICT_BED_R = 4;     // arredondamento do canto dos canteiros

/** (x,z) está DENTRO de um canteiro (retângulo de cantos arredondados)? */
function inBed(bed, x, z) {
  const g = bed.grass || bed; // MESMO retângulo justo (26×26) desenhado pelo CityTexture
  if (x <= g.left || x >= g.right || z <= g.top || z >= g.bottom) return false;
  const r = DISTRICT_BED_R;
  const qx = Math.max(0, g.left + r - x, x - (g.right - r));
  const qz = Math.max(0, g.top + r - z, z - (g.bottom - r));
  return qx * qx + qz * qz <= r * r;
}

/**
 * PASSARELAS de uma superquadra num eixo: as FRONTEIRAS INTERNAS entre canteiros
 * vizinhos. O canteiro tem 32 e a grama 26 (districtBed), então cada fronteira é o
 * MEIO EXATO de um vão de DISTRICT_PATH (6u) de piso — é por ele que o pedestre
 * circula. As fronteiras EXTERNAS (a borda do bloco de canteiros) não entram: ali o
 * caminho é o anel do perímetro.
 *
 * Sai da geometria dos canteiros, e não dos eixos das ruas que sumiram (`d.axisX/Z`):
 * com os canteiros empacotados os dois deixaram de coincidir — o eixo cai RENTE à
 * grama, não no meio do vão —, e era daí que vinha a malha passando por dentro de
 * prédio. Um lote MESCLADO (o shopping, 2 colunas) simplesmente não contribui com a
 * fronteira que ele engoliu; a linha continua existindo por causa dos vizinhos e o
 * SidewalkGraph a recorta no trecho tapado (ver `run`).
 */
function districtLanes(beds, lo, hi) {
  const v = new Set();
  // arredonda em 0,01 antes de unificar: a mesma fronteira chega por dois lotes
  // (a direita de um, a esquerda do outro) e um bit de diferença viraria DUAS
  // passarelas coladas em vez de uma
  const q = (n) => Math.round(n * 100) / 100;
  for (const bed of beds) { v.add(q(bed[lo])); v.add(q(bed[hi])); }
  const all = [...v].sort((a, b) => a - b);
  return all.slice(1, -1); // tira as duas bordas externas do bloco de canteiros
}

/**
 * MALHA DE CAMINHADA de cada superquadra: as LINHAS DE CENTRO do piso — o anel do
 * perímetro (calçada de 5u) e UMA PASSARELA POR VÃO entre canteiros vizinhos, nos dois
 * eixos. É a "planta" que o SidewalkGraph transforma em grafo e que o Parking usa para
 * levar o pedestre da posição dele até o caminho mais próximo.
 *
 * Antes havia UMA cruz só, nos eixos das ruas que sumiram, mais o anel de um largo
 * central e um ESPIGÃO da porta de cada prédio. Com os canteiros empacotados isso não
 * descreve mais o piso: sobravam vãos inteiros sem caminho (o pedestre tinha de
 * contornar a superquadra) e o anel do largo cortava a pegada dos prédios do miolo. Os
 * espigões saíram junto — o CityTexture parou de pintá-los na mesma revisão que tirou o
 * largo, então o que restou deles era pedestre andando sobre a grama do canteiro
 * (MEDIDO: 0,15% dos ticks de pessoa). A malha agora é a GRADE dos vãos REAIS, e nada
 * mais: é exatamente o negativo do que o chão mostra.
 */
export const DISTRICT_WALK = DISTRICTS.map((d) => {
  const L = d.land;
  const half = SIDEWALK / 2;
  const beds = DISTRICT_BEDS.filter((bed) => bed.district === d);
  return {
    d,
    beds,
    x0: L.left + half, x1: L.right - half,
    z0: L.top + half, z1: L.bottom - half,
    xLines: districtLanes(beds, 'left', 'right'),
    zLines: districtLanes(beds, 'top', 'bottom')
  };
});
const WALK_OF = new Map(DISTRICT_WALK.map((w) => [w.d, w]));

/** Malha de caminhada da superquadra de um distrito (ou de um canteiro). */
export function districtWalk(d) {
  return WALK_OF.get(d) || null;
}

/**
 * (x,z) está sobre o PISO de uma superquadra (calçada/passarela)? Devolve a malha do
 * distrito, ou `null`. É o NEGATIVO do desenho, na mesma ordem do
 * CityTexture.drawDistrictBlocks: pavimenta a superquadra inteira e tira os canteiros.
 * Nada é devolvido por cima da grama — quem anda aqui anda no que está pintado de
 * calçada, e só.
 */
export function districtPavement(x, z) {
  for (const W of DISTRICT_WALK) {
    const L = W.d.land;
    if (x <= L.left || x >= L.right || z <= L.top || z >= L.bottom) continue;
    for (const bed of W.beds) if (inBed(bed, x, z)) return null;
    return W;
  }
  return null;
}

/**
 * Ponto do CAMINHO mais próximo de (x,z) — o "portão" de quem está parado no piso da
 * superquadra. Como TODO trecho de piso é um vão de 6u (ou o anel do perímetro) com
 * uma linha de centro no meio, o portão está a no máximo 3,5u e a perna portão↔posição
 * é PERPENDICULAR à linha, então corre por dentro do próprio vão: nunca sai do piso e
 * nunca cruza um prédio (é o mesmo papel do personSpotGate numa quadra comum, só que a
 * planta aqui é a da superquadra).
 *
 * É por isso que a malha precisa ter UMA linha por vão: com a cruz única de antes, um
 * pedestre no vão sem caminho recebia um portão do outro lado da superquadra e a perna
 * "curta" virava uma reta de dezenas de unidades atravessando os prédios do meio.
 */
export function districtWalkGate(W, x, z) {
  const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
  const zc = clamp(z, W.z0, W.z1), xc = clamp(x, W.x0, W.x1);
  const cand = [
    { x: W.x0, z: zc },
    { x: W.x1, z: zc },
    { x: xc, z: W.z0 },
    { x: xc, z: W.z1 }
  ];
  for (const xl of W.xLines) cand.push({ x: xl, z: zc });
  for (const zl of W.zLines) cand.push({ x: xc, z: zl });
  let best = cand[0], bd = Infinity;
  for (const c of cand) {
    const d = (c.x - x) ** 2 + (c.z - z) ** 2;
    if (d < bd) { bd = d; best = c; }
  }
  return best;
}

/**
 * Id de grade de um prédio (notação A1..C5): LETRA = linha (A/B/C/D = r0..r3),
 * NÚMERO = coluna+1 (1..5 = c0..c4). Único por prédio (âncora c0,r0), estável e
 * independente da ordem do array — é o identificador usado no protocolo. A grade
 * varia por cidade (5×3 nas três originais, 4×4 na Mexican Town).
 */
export function buildingGridId(b) {
  return String.fromCharCode(65 + b.r0) + (b.c0 + 1);
}
/** índice (em BUILDINGS) → id de grade. Ex.: BUILDING_IDS[5] === 'B3'. */
export const BUILDING_IDS = BUILDINGS.map(buildingGridId);
/** id de grade → índice (inverso de BUILDING_IDS). */
export const BUILDING_INDEX = new Map(BUILDING_IDS.map((id, i) => [id, i]));

/**
 * PARQUE de superquadra → id do distrito. Chegar num destes é PASSEIO, não parada: o
 * avatar circula pelo distrito em vez de estacionar em frente, como faz na praça das
 * outras cidades. A Mexican Town não tem rotatória; o `park` de cada distrito é o
 * equivalente dela ali (um por distrito).
 *
 * A chave é o id de GRADE do parque, e não o id do distrito, de propósito: é ele que
 * viaja no `goto`/`arrived`, então o dedup 1×/edifício, o progresso do ranking e o
 * mapeamento das ativações RSVP continuam funcionando sem nenhuma mudança no servidor.
 */
export const STROLL_DISTRICT = new Map(
  BUILDINGS
    .map((b, i) => [BUILDING_IDS[i], b])
    .filter(([, b]) => b.type === 'park' && b.bed && b.bed.district)
    .map(([id, b]) => [id, b.bed.district.id])
);

/** Este destino é um parque de superquadra (→ passeio pelo distrito)? */
export function strollDistrictOf(buildingId) {
  return STROLL_DISTRICT.get(buildingId) ?? null;
}

/** Dois edifícios estão na MESMA quadra ou em quadras VIZINHAS (inclui diagonais
 *  — distância de Chebyshev ≤ 1 entre as quadras)? Decide caminhar × dirigir. */
export function buildingsAreNeighbors(i, j) {
  if (i === j) return true;
  const a = BUILDINGS[i], b = BUILDINGS[j];
  if (!a || !b) return false;
  const colGap = Math.max(0, a.c0 - b.c1, b.c0 - a.c1);
  const rowGap = Math.max(0, a.r0 - b.r1, b.r0 - a.r1);
  return colGap <= 1 && rowGap <= 1;
}

/**
 * Bancos no gramado em frente a cada prédio (2 por prédio, ao lado da entrada).
 * Fonte única: Benches.js os desenha e generateTrees evita posicionar arbustos
 * em cima deles.
 */
export const BENCHES = [];
for (const b of BUILDINGS) {
  if (b.type === 'shopping') continue; // shopping usa totens/banners, sem bancos
  const z = b.z + b.d / 2 + 2.6; // gramado, à frente do prédio
  // escola: bancos mais para os CANTOS (não conflitar com o pórtico central)
  const offX = b.type === 'school' ? b.w / 2 - 0.5 : Math.min(b.w * 0.34, 7);
  // clínica usa PLACAS DE CHÃO no lugar dos bancos; escola infantil (pátio com
  // cerquinha), hotel resort (tapete/carrinho), fintech e café (terraço próprio
  // com mesinhas/guarda-sóis) já têm frente própria: mantém as posições (árvores
  // continuam evitando) mas não desenha o banco
  const noDraw =
    b.type === 'clinic' ||
    b.type === 'kinder' ||
    b.type === 'resort' ||
    b.type === 'fintech' ||
    b.type === 'bullion' ||
    b.type === 'ecommerce' ||
    b.type === 'cafe';
  BENCHES.push({ x: b.x - offX, z, noDraw }, { x: b.x + offX, z, noDraw });
}

/**
 * Zona livre em frente à porta (entrada na face +Z, centrada em b.x). Usada
 * para NÃO posicionar árvores/arbustos obstruindo a porta — um corredor da
 * fachada até a rua.
 */
export const DOORS = [];
for (const b of BUILDINGS) {
  const isHouse = b.type === 'house' || b.type === 'houses';
  const doorW = isHouse ? Math.min(b.w * 0.28, 3.2) : Math.min(b.w * 0.34, 9);
  const halfW = doorW / 2 + 1.6; // meia-largura do corredor (porta + folga)
  const reach = SIDEWALK + PARK + 1; // da fachada até a calçada/rua
  const hw = b.w / 2;
  const hd = b.d / 2;
  // corredor livre em frente à porta da face +Z (padrão)
  DOORS.push({ x0: b.x - halfW, x1: b.x + halfW, z0: b.z + hd - 2, z1: b.z + hd + reach });
  // Fintech tem ENTRADA nas 4 FACES → corredor livre também nas outras 3
  if (b.type === 'fintech') {
    DOORS.push({ x0: b.x - halfW, x1: b.x + halfW, z0: b.z - hd - reach, z1: b.z - hd + 2 });
    DOORS.push({ x0: b.x + hw - 2, x1: b.x + hw + reach, z0: b.z - halfW, z1: b.z + halfW });
    DOORS.push({ x0: b.x - hw - reach, x1: b.x - hw + 2, z0: b.z - halfW, z1: b.z + halfW });
  }
}

/**
 * Ocupação da grade (coluna,linha) por quadra "viva" — usado para decidir
 * faixas de pedestre apenas entre quadras vizinhas populadas. Inclui a praça.
 */
const _occupied = new Set();
for (const lot of LOTS) {
  for (let c = lot.c0; c <= lot.c1; c++) {
    for (let r = lot.r0; r <= lot.r1; r++) _occupied.add(`${c},${r}`);
  }
}
if (RB) _occupied.add(`${RB.c},${RB.r}`); // praça (quadra populada, com calçada/fonte)
export const GRID_COLS = V.length - 1;
export const GRID_ROWS = H.length - 1;
export function isPopulated(col, row) {
  return (
    col >= 0 &&
    col < GRID_COLS &&
    row >= 0 &&
    row < GRID_ROWS &&
    _occupied.has(`${col},${row}`)
  );
}

export const PARKS = [];
for (const pc of PARK_CELLS) {
  const land = landRect(pc.c, pc.c, pc.r, pc.r);
  PARKS.push(land);
  BLOCKS.push({ land, kind: 'park' });
}

// Quadra da rotatória (sem prédio) — também recebe calçada/borda arredondada
if (RB) BLOCKS.push({ land: landRect(RB.c, RB.c, RB.r, RB.r), kind: 'plaza' });


/** Rotatória na quadra da praça desta cidade (cities.js → roundabout), ou `null`
 *  se a cidade não tem praça — quem desenha/navega a praça checa antes. */
export const ROUNDABOUT = RB
  ? (() => {
      const l = landRect(RB.c, RB.c, RB.r, RB.r);
      return { x: l.x, z: l.z, r: 7 };
    })()
  : null;

/**
 * PRAÇA como DESTINO de primeira classe. Tem um id de grade próprio (célula-âncora
 * da rotatória, notação A1..C5 — em Acquisition é `C2`), como os prédios, para o
 * backend poder mandar avatares até ela via `goto`. É ESPECIAL: diferente de um
 * prédio, os avatares NÃO estacionam em frente — eles ENTRAM e passeiam pelos
 * caminhos internos (cruz + largo da fonte). A geometria navegável mora no
 * SidewalkGraph; o comportamento, no CityAgents (estado `plaza`).
 *
 * `null` nas cidades SEM praça (Mexican Town): ali o destino especial não existe
 * e todo mundo que o consome (backends, Decor, CityTexture) checa antes de usar.
 */
export const PLAZA_ID = RB ? String.fromCharCode(65 + RB.r) + (RB.c + 1) : null;
export const PLAZA = RB
  ? (() => {
      const land = landRect(RB.c, RB.c, RB.r, RB.r);
      return { id: PLAZA_ID, x: land.x, z: land.z, r: ROUNDABOUT.r, col: RB.c, row: RB.r, land };
    })()
  : null;

/** É o id de grade da praça desta cidade? (destino especial, não é um prédio.)
 *  Sempre `false` numa cidade sem praça — inclusive para `id` nulo/indefinido. */
export function isPlazaId(id) {
  return PLAZA_ID != null && id === PLAZA_ID;
}

/** O prédio `bi` está na MESMA quadra ou VIZINHA da praça? (decide a pé × dirigir,
 *  espelhando buildingsAreNeighbors, mas contra a célula da praça.) */
export function buildingNeighborsPlaza(bi) {
  if (!RB) return false; // cidade sem praça
  const b = BUILDINGS[bi];
  if (!b) return false;
  const colGap = Math.max(0, b.c0 - RB.c, RB.c - b.c1);
  const rowGap = Math.max(0, b.r0 - RB.r, RB.r - b.r1);
  return colGap <= 1 && rowGap <= 1;
}

/**
 * Vias laterais "infinitas" — pontos de entrada/saída de jogadores (da cidade
 * ativa). Quando o backend anunciar um novo jogador, ele surge na ponta
 * off-screen de uma destas vias (ENTRY_POINTS) e entra dirigindo; ao sair da
 * lista, segue para fora pela mesma via. (z=-23 só aceita 'left' — à direita
 * ela termina no prédio alto.)
 */
export const ENTRY_ROADS = ACTIVE_CITY.entryRoads;

/**
 * Vias de entrada VERTICAIS (em cima/embaixo) — mesma ideia das laterais, mas
 * nas vias verticais que se prolongam para fora do mapa pela borda superior e
 * inferior. Da cidade ativa.
 */
export const ENTRY_ROADS_V = ACTIVE_CITY.entryRoadsV;

/** Pontos de surgimento (na borda do mundo), com heading apontando p/ dentro. */
export const ENTRY_POINTS = [
  ...ENTRY_ROADS.flatMap((er) =>
    er.sides.map((side) => ({
      side,
      x: side === 'left' ? -WORLD.width / 2 : WORLD.width / 2,
      z: er.z,
      heading: side === 'left' ? Math.PI / 2 : -Math.PI / 2 // +X (esq) / -X (dir)
    }))
  ),
  ...ENTRY_ROADS_V.flatMap((ev) =>
    ev.sides.map((side) => ({
      side,
      x: ev.x,
      z: side === 'top' ? -WORLD.depth / 2 : WORLD.depth / 2,
      heading: side === 'top' ? 0 : Math.PI // +Z (cima) / -Z (baixo)
    }))
  )
];

/* ------------------------------------------------------------------ */
/*  Distribuição de árvores                                            */
/* ------------------------------------------------------------------ */

/**
 * Árvores em três grupos, todos respeitando as calçadas:
 *   - na CALÇADA de cada quadra (espaçadas, tipo arborização de rua)
 *   - no PARQUE interno (anel verde ao redor do prédio / quadra-parque cheia)
 *   - no cinturão de FLORESTA fora das vias externas
 * Nenhuma árvore cai sobre as ruas.
 */
export function generateTrees(worldWidth, worldDepth, rand) {
  const trees = [];
  // não posiciona arbusto colidindo com um banco (caixa um pouco maior que o
  // assento de 3,4 × 1,1, com folga para a copa)
  const nearBench = (x, z) =>
    BENCHES.some((bn) => Math.abs(x - bn.x) < 3.0 && Math.abs(z - bn.z) < 2.4);
  // corredor livre em frente à porta (não obstruir a entrada)
  const inFrontOfDoor = (x, z) =>
    DOORS.some((dr) => x > dr.x0 && x < dr.x1 && z > dr.z0 && z < dr.z1);
  // nunca planta dentro da pegada de um prédio (a folga vale p/ lote mesclado, onde
  // o canteiro é bem mais justo que o quadrado dos demais)
  const inBuilding = (x, z) =>
    BUILDINGS.some(
      (b) => Math.abs(x - b.x) < b.w / 2 + 0.6 && Math.abs(z - b.z) < b.d / 2 + 0.6
    );
  // (não existe mais exclusão do "largo" central do distrito: o largo, a fonte e o
  //  banner do eixo foram removidos — ver Decor.createDecor. O que sobrou ali é grama
  //  de canteiro como qualquer outra, e vegetação nela é o certo.)
  const push = (x, z, scale, dark) => {
    if (nearBench(x, z)) return;
    if (inFrontOfDoor(x, z)) return;
    if (inBuilding(x, z)) return;
    trees.push({
      x, z, scale, tint: pickTint(rand(), dark), rot: rand() * Math.PI * 2
    });
  };

  for (const block of BLOCKS) {
    const land = block.land;

    // SUPERQUADRA: a vegetação acompanha o CANTEIRO do prédio (que é centrado nele),
    // não a célula da grade — senão as fieiras marcam as ruas que deixaram de existir
    // e ficam tortas em relação ao prédio. Dois anéis: arbustos na beira do gramado
    // (junto às passarelas) e árvores encostando no prédio.
    if (block.bed) {
      const g = block.bed.grass || block.bed; // ancora na grama JUSTA (26×26), não na borda do canteiro
      // árvores encostando no prédio: inset 2.0 da borda da grama (~11 do centro) — sobra
      // ~2u até a parede (guarda inBuilding = b.w/2+0.6 = 9.6), mesmo anel do Acquisition
      placeAlongPerimeter(g, 2.0, 7, 5, rand, (x, z) =>
        push(x, z, 0.5 + rand() * 0.35)
      );
      // arbustos na beira da grama, junto à calçada (inset 0.8 ⇒ ~12.2 do centro)
      placeAlongPerimeter(g, 0.8, 9, 5, rand, (x, z) =>
        push(x, z, 0.42 + rand() * 0.22)
      );
      continue;
    }

    // 1) Árvores de calçada — no lado INTERNO da calçada (longe do meio-fio).
    //    Os carros agora estacionam encostados no meio-fio (dentro da calçada),
    //    então as árvores ficam mais para dentro para não sobrepor os carros.
    const curbInset = 3.8; // bem para dentro da calçada (era 1.5, junto à rua)
    placeAlongPerimeter(land, curbInset, 10, 6, rand, (x, z) =>
      push(x, z, 0.42 + rand() * 0.22)
    );

    if (block.kind === 'park') {
      // 2a) Quadra-parque: preenchimento interno (dentro da calçada)
      const inner = inset(land, SIDEWALK + 1.5);
      fillRectJittered(inner, 6.5, rand, (x, z) =>
        push(x, z, 0.6 + rand() * 0.5)
      );
    } else if (block.kind === 'building') {
      // 2b) Anel de parque ao redor do prédio (entre calçada e prédio)
      const ringInset = SIDEWALK + PARK / 2;
      placeAlongPerimeter(land, ringInset, 7, 5, rand, (x, z) =>
        push(x, z, 0.5 + rand() * 0.35)
      );
    } else if (block.kind === 'plaza') {
      // 2c) Praça: árvores nos 4 quadrantes (fora da fonte central e dos
      //     caminhos em cruz)
      const cxp = (land.left + land.right) / 2;
      const czp = (land.top + land.bottom) / 2;
      for (let i = 0; i < 30; i++) {
        const x = (rand() * 2 - 1) * 11;
        const z = (rand() * 2 - 1) * 11;
        if (Math.hypot(x, z) < 9) continue; // longe da fonte
        if (Math.abs(x) < 2.8 || Math.abs(z) < 2.8) continue; // fora dos caminhos
        push(cxp + x, czp + z, 0.45 + rand() * 0.35);
      }
    }
  }

  // 3) Cinturão de floresta fora das vias externas
  forestBelt(worldWidth, worldDepth, rand, push);

  return trees;
}

/** Coloca pontos ao longo do perímetro de um retângulo (inset da borda),
 *  espaçados por ~spacing, deixando uma folga (corner) nas quinas. */
function placeAlongPerimeter(land, insetAmt, spacing, corner, rand, cb) {
  const r = inset(land, insetAmt);
  const sides = [
    { x0: r.left, z0: r.top, x1: r.right, z1: r.top }, // topo
    { x0: r.left, z0: r.bottom, x1: r.right, z1: r.bottom }, // base
    { x0: r.left, z0: r.top, x1: r.left, z1: r.bottom }, // esquerda
    { x0: r.right, z0: r.top, x1: r.right, z1: r.bottom } // direita
  ];
  for (const s of sides) {
    const len = Math.hypot(s.x1 - s.x0, s.z1 - s.z0);
    const usable = len - corner * 2;
    if (usable <= 0) continue;
    const n = Math.max(1, Math.round(usable / spacing));
    for (let i = 0; i <= n; i++) {
      const t = (corner + (usable * i) / n) / len;
      const jx = (rand() - 0.5) * 1.2;
      const jz = (rand() - 0.5) * 1.2;
      cb(s.x0 + (s.x1 - s.x0) * t + jx, s.z0 + (s.z1 - s.z0) * t + jz);
    }
  }
}

/** Preenche um retângulo com uma grade jitterada de pontos. */
function fillRectJittered(r, spacing, rand, cb) {
  for (let x = r.left + spacing / 2; x < r.right; x += spacing) {
    for (let z = r.top + spacing / 2; z < r.bottom; z += spacing) {
      cb(x + (rand() - 0.5) * spacing * 0.5, z + (rand() - 0.5) * spacing * 0.5);
    }
  }
}

function forestBelt(worldWidth, worldDepth, rand, push) {
  const halfW = worldWidth / 2;
  const halfD = worldDepth / 2;
  const outerX = V[V.length - 1] + HALF + 4; // além da via externa
  const outerZ = H[H.length - 1] + HALF + 4;
  const bx = V[V.length - 1] + HALF; // limite lateral do mapa (120)
  const bz = H[H.length - 1] + HALF; // limite superior/inferior do mapa (74)
  const clear = ROADS.width / 2 + 3.5; // folga das árvores em relação à rua

  // não coloca árvore sobre (nem encostando em) as vias de entrada
  const onEntryRoad = (x, z) =>
    ENTRY_ROADS.some(
      (er) =>
        Math.abs(z - er.z) < clear &&
        er.sides.some(
          (s) => (s === 'left' && x < -bx + 9) || (s === 'right' && x > bx - 9)
        )
    ) ||
    ENTRY_ROADS_V.some(
      (ev) =>
        Math.abs(x - ev.x) < clear &&
        ev.sides.some(
          (s) => (s === 'top' && z < -bz + 9) || (s === 'bottom' && z > bz - 9)
        )
    );

  // Espalha N árvores num retângulo (raio rx,rz), pulando a cidade e as ruas.
  // dark=true → tom de verde mais escuro (floresta fora da cidade), destacando
  // a cidade no centro.
  const scatter = (n, rx, rz, sMin, sVar) => {
    for (let i = 0; i < n; i++) {
      const x = (rand() * 2 - 1) * rx;
      const z = (rand() * 2 - 1) * rz;
      if (Math.abs(x) < outerX && Math.abs(z) < outerZ) continue; // dentro da cidade
      if (onEntryRoad(x, z)) continue; // sobre uma via de entrada
      push(x, z, sMin + rand() * sVar, true);
    }
  };

  // Floresta tropical envolvendo a cidade: anel DENSO junto à borda + mata que
  // se estende bem para fora (some no fog), dando a sensação de cerco verde.
  scatter(2000, halfW + 6, halfD + 6, 0.7, 1.0);
  scatter(1200, halfW + 74, halfD + 70, 0.8, 1.2);
}

function inset(r, a) {
  return {
    left: r.left + a,
    right: r.right - a,
    top: r.top + a,
    bottom: r.bottom - a
  };
}

function pickTint(r, dark) {
  if (dark) {
    // tons mais escuros para a floresta externa (destaca a cidade no centro)
    if (r < 0.34) return '#1c4d2b';
    if (r < 0.67) return '#143c22';
    return '#235c37';
  }
  if (r < 0.34) return COLORS.treeA;
  if (r < 0.67) return COLORS.treeB;
  return COLORS.treeC;
}

/** PRNG determinístico (mulberry32). */
export function makeRandom(seed = 20260619) {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
